import { shallowEqual } from "react-redux";
import type { MutableRefObject } from "react";
import { useEffect, useMemo, useState } from "react";
import { PdpSection, scrollTargetIndexMap, ScrollTargetSelector } from "../utils/constants";
import type { UscLabelType } from "../utils/labels";
import { useUscSelector } from "../redux/store";
import { pdpSectionSelector } from "../utils/pdpUtils";
import { getActiveScrollTarget } from "../utils/helpers";
import { getScrollBehavior, isMobileView, isRetailer } from "../../../common-deprecated/utils";
import { useAemNavOffset } from "../../../common-deprecated/hooks";

export const scrollSelectorsLabelMap: Record<ScrollTargetSelector, UscLabelType> = {
    [ScrollTargetSelector.Highlights]: "uscPdpMenuHighlights",
    [ScrollTargetSelector.Services]: "uscPdpMenuServices",
    [ScrollTargetSelector.Accessories]: "uscPdpMenuAccessories",
    [ScrollTargetSelector.CarDetails]: "uscPdpMenuCarDetails",
    [ScrollTargetSelector.DealerInfo]: "uscPdpMenuDealer",
};

export const scrollSelectorSectionMap: Record<ScrollTargetSelector, PdpSection> = {
    [ScrollTargetSelector.Highlights]: PdpSection.Highlights,
    [ScrollTargetSelector.Services]: PdpSection.Services,
    [ScrollTargetSelector.Accessories]: PdpSection.Accessories,
    [ScrollTargetSelector.CarDetails]: PdpSection.CarDetails,
    [ScrollTargetSelector.DealerInfo]: PdpSection.DealerInfo,
};

const selectorSectionIsVisible = (selector: ScrollTargetSelector, pdpSectionCollection: PdpSection[]): boolean => {
    return !!pdpSectionCollection.find((section) => scrollSelectorSectionMap[selector] === section);
};

type UseMenuSectionSelectorsType = {
    selectors: ScrollTargetSelector[];
    scrollSelectorsLabelMap: Record<ScrollTargetSelector, UscLabelType>;
    scrollTo: (selector: ScrollTargetSelector) => void;
};

const useMenuSectionSelectors = (
    HORIZONTAL_MENU_HEIGHT: number,
    BREADCRUMBS_HEIGHT: number,
    EXTRA_DETECTION_MARGIN: number,
    ARROW_WIDTH: number,
    EXTRA_SCROLL_MARGIN: number,
    isScrolling?: MutableRefObject<boolean>,
): UseMenuSectionSelectorsType => {
    const [navHeight, setNavHeight] = useState(0);
    const visiblePdpSections = useUscSelector(pdpSectionSelector, shallowEqual);
    const navOffset = useAemNavOffset();
    const commonSettings = useUscSelector((state) => state.commonSettings);

    // Disable menu items when content isn't displayed
    const selectors = useMemo(() => {
        return Object.values(ScrollTargetSelector)
            .filter((selector) => {
                if (!selectorSectionIsVisible(selector, visiblePdpSections)) return false;

                return true;
            })
            .sort((a, b) => {
                // Make sure the sorting matches the mainIndex configuration.
                const mainSectionIndexA = visiblePdpSections.findIndex(
                    (section) => scrollSelectorSectionMap[a] === section,
                );
                const mainSectionIndexB = visiblePdpSections.findIndex(
                    (section) => scrollSelectorSectionMap[b] === section,
                );

                return mainSectionIndexA - mainSectionIndexB;
            });
    }, [visiblePdpSections]);

    const scrollTo = (selector: ScrollTargetSelector): void => {
        if (isScrolling?.current) return;
        const element = document.getElementById(selector);

        if (element) {
            const offset = navHeight + BREADCRUMBS_HEIGHT + HORIZONTAL_MENU_HEIGHT + EXTRA_DETECTION_MARGIN;
            // The index of the scroll target where we will scroll to
            const newScrollTargetIndex = scrollTargetIndexMap[selector];
            const activeScrollTarget = getActiveScrollTarget(selectors, offset);
            // The index of the scroll target that's currently in view
            const previousScrollTargetIndex = activeScrollTarget ? scrollTargetIndexMap[activeScrollTarget] : 0;

            const isScrollingUp = newScrollTargetIndex <= previousScrollTargetIndex;

            // Fetch user preferences for motion
            const behavior = getScrollBehavior();
            const isMobile = isMobileView();
            // The nav is only shown when scrolling up. Add that offset when scrolling up
            const scrollOffset = isScrollingUp ? navHeight : 0;

            let top: number;
            if (isRetailer(commonSettings)) {
                top =
                    element.getBoundingClientRect().top +
                    window.scrollY -
                    // The horizontal menu is not visible on mobile
                    HORIZONTAL_MENU_HEIGHT -
                    EXTRA_SCROLL_MARGIN;
            } else {
                top =
                    element.getBoundingClientRect().top +
                    window.scrollY -
                    BREADCRUMBS_HEIGHT -
                    // The horizontal menu is not visible on mobile
                    (isMobile ? 0 : HORIZONTAL_MENU_HEIGHT) -
                    scrollOffset -
                    (isMobile ? EXTRA_SCROLL_MARGIN / 2 : EXTRA_SCROLL_MARGIN);
            }

            try {
                window.scrollTo({
                    top,
                    behavior,
                });

                // Scroll behaviour edge case
                // The scroll doesn't trigger a scroll in case the behaviour is not smooth (depending on by getScrollBehavior)
                // For this edge case we manually trigger a scroll event to trigger the onscroll logic which updates the selected pills
                if (behavior !== "smooth") {
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent("scroll"));
                    }, 500);
                }
            } catch (ignored) {
                // For retailer screens specifically: using the behavior flag on Tizen 4 throws an error.
                // Instead we do this and add the CSS rule "scroll-behaviour:smooth" on the viewport.
                window.scroll(0, top);
            }
        }
    };

    // Keeps track of the latest navOffset when the navigation was open,
    // so it can also be used when the navigation is closed
    // Keep in mind that the offset will change between desktop and mobile
    useEffect(() => {
        if (navOffset !== 0) {
            setNavHeight(navOffset);
        }
    }, [navOffset]);

    return { selectors, scrollSelectorsLabelMap, scrollTo };
};

export default useMenuSectionSelectors;
